/**
 * Puts the CRM's activities on the signed-in person's calendar (calender.cilpron.com).
 *
 *   const liveCrm = withCalendar(be.crm, (msg) => say(msg, true));
 *
 * It wraps the CRM client, so every place that creates, edits, reschedules, cancels or deletes an activity (the Activity screen, a contact's
 * page, the dashboard) is covered without touching those screens. The activity is saved first; the calendar is updated after, in the
 * background, and a failure there never undoes or blocks the CRM save (the message goes to onError instead).
 *
 *  - Calls, Meetings and Tasks that are Open appear on a calendar called "CRM" (created on first use). Emails, SMS and chats do not.
 *  - Time: the activity's own time when it has one (dueAt), otherwise 09:00 on its due date. A meeting lasts 60 minutes, a call or task 30.
 *  - Reschedule or edit moves the same calendar event. Cancel or delete removes it. Complete leaves it where it was.
 *  - The calendar event carries source "crm", related type "activity" and the activity's id, so it is found again by that and never duplicated.
 *  - If the person has connected Google or Outlook in the calendar, the event goes there too (the calendar service does that).
 */
import type { CrmClient, Row } from "./crmClient";

const CAL_BASE_URL = "https://calender.cilpron.com";
const SOURCE = "crm";
const RELATED = "activity";
const MINUTES: Record<string, number> = { Meeting: 60, Call: 30, Task: 30 };
const DAY = 86400000;

const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos";
  } catch {
    return "Africa/Lagos";
  }
})();

const getToken = (): string | null => {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

const enc = encodeURIComponent;

async function call<T>(method: "GET" | "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const token = getToken();
  if (!token) throw new Error("not signed in");
  const res = await fetch(`${CAL_BASE_URL}/api/calendar${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) throw new Error("the calendar did not accept your sign-in");
  if (res.status === 403) throw new Error("no permission on the calendar");
  if (!res.ok) throw new Error(`the calendar answered ${res.status}`);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/* the person's "CRM" calendar: found once, created if it is not there yet (remembered per sign-in) */
let cached: { token: string; id: Promise<string> } | null = null;
function crmCalendarId(): Promise<string> {
  const token = getToken() ?? "";
  if (cached && cached.token === token) return cached.id;
  const id = (async () => {
    const list = await call<{ id: string; kind?: string; role?: string }[]>("GET", "/calendars");
    const mine = list.find((c) => (c.kind ?? "").toLowerCase() === "crm" && (c.role ?? "").toLowerCase() === "owner");
    if (mine) return mine.id;
    const made = await call<{ id: string }>("POST", "/calendars", { name: "CRM", kind: "crm", color: "#c98a00", timeZone: TZ });
    return made.id;
  })();
  cached = { token, id };
  id.catch(() => {
    if (cached && cached.id === id) cached = null;
  });
  return id;
}

async function findEvent(activityId: string): Promise<string | null> {
  const from = new Date(Date.now() - 190 * DAY).toISOString();
  const to = new Date(Date.now() + 209 * DAY).toISOString(); // the service accepts at most 400 days at a time
  const list = await call<{ id: string }[]>("GET", `/feed?from=${enc(from)}&to=${enc(to)}&source=${SOURCE}&relatedType=${RELATED}&relatedId=${enc(activityId)}&invited=false`);
  return list[0]?.id ?? null;
}

/** When the activity happens. A date-only activity (or a time of exactly midnight UTC, how a date is often stored) becomes 09:00 local. */
function startOf(row: Row): Date | null {
  const dueAt = String(row.dueAt ?? "");
  if (dueAt && !Number.isNaN(Date.parse(dueAt)) && !/T00:00:00(\.0+)?(Z|\+00:00)?$/.test(dueAt)) return new Date(dueAt);
  const [y, m, d] = String(row.date ?? "").split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d, 9, 0) : null;
}

async function sync(row: Row): Promise<void> {
  const type = String(row.type ?? "");
  const status = String(row.status ?? "");
  const existing = await findEvent(row.id);
  if (status === "Completed") return; // done: leave the event where it was
  if (MINUTES[type] === undefined || status === "Cancelled") {
    if (existing) await call("DELETE", `/events/${existing}?purge=true`);
    return;
  }
  const start = startOf(row);
  if (!start) return;
  const body = {
    title: `${type}: ${String(row.subject ?? "")}`.slice(0, 1000),
    description: row.number ? `CRM activity ${String(row.number)}` : "CRM activity",
    startAt: start.toISOString(),
    endAt: new Date(start.getTime() + MINUTES[type] * 60000).toISOString(),
    isAllDay: false,
    timeZone: TZ,
    source: SOURCE,
    relatedType: RELATED,
    relatedId: String(row.id),
  };
  if (existing) await call("PATCH", `/events/${existing}?scope=all`, body);
  else await call("POST", "/events", { ...body, calendarId: await crmCalendarId() });
}

async function drop(activityId: string): Promise<void> {
  const existing = await findEvent(activityId);
  if (existing) await call("DELETE", `/events/${existing}?purge=true`);
}

/** The CRM client with calendar updates added after every activity change. Everything else passes through unchanged. */
export function withCalendar(client: CrmClient, onError?: (message: string) => void): CrmClient {
  const run = (job: Promise<void>) => {
    if (!getToken()) return;
    job.catch((e: unknown) => onError?.(`Saved, but the calendar was not updated: ${e instanceof Error ? e.message : "unknown error"}.`));
  };
  const out = { ...client } as CrmClient;
  out.create = async (src, values) => {
    const row = await client.create(src, values);
    if (src === "activities") run(sync(row));
    return row;
  };
  out.update = async (src, row, values) => {
    const next = await client.update(src, row, values);
    if (src === "activities") run(sync(next));
    return next;
  };
  out.action = async (src, row, path, body) => {
    const next = await client.action(src, row, path, body);
    if (src === "activities") {
      if (path === "cancel") run(drop(row.id));
      else if (path === "reschedule") run(sync(next));
    }
    return next;
  };
  out.remove = async (src, row) => {
    await client.remove(src, row);
    if (src === "activities") run(drop(row.id));
  };
  return out;
}